import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { TextField, Alert } from "@mui/material";
import { toast } from "sonner";
import { toastApiError } from "@/api/client";
import { useAuthStore } from "@/modules/auth";
import { useMarkIn, useTodayAttendance } from "@/api/hooks/attendance";
import { useMe } from "@/api/hooks/auth";
import { useGeolocation } from "../hooks/useGeolocation";
import { SlideToConfirm } from "@/shared/components/SlideToConfirm";
import {
  asGeofenceError,
  asGeofenceAmbiguousError,
  haversineM,
  type AmbiguousCandidate,
  type GeofenceErrorDetails,
} from "../utils/geofenceError";
import { LocationPickerDialog } from "./LocationPickerDialog";
import { GeofenceLiveMap, type FencePoint } from "./GeofenceLiveMap";

/**
 * Permanent prompt forcing the user to mark-in attendance after login.
 * Cannot be dismissed until /attendance/today returns a row.
 * UI ported from assets/workman-dashboard redesign — wm-modal* + wm-mi*
 * classes (see styles/wm-redesign.css). REQUIRED badge in header (no close
 * button); slide-to-confirm in footer.
 */
export function AttendanceGuardModal() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const enabled =
    role === "admin" || role === "operations" || role === "employee";

  const att = useTodayAttendance(enabled);
  const me = useMe(enabled);
  const markIn = useMarkIn();
  const geo = useGeolocation();
  const navigate = useNavigate();
  const location = useLocation();
  const myLocations = me.data?.locations ?? [];
  const fences: FencePoint[] = myLocations.map((loc) => ({
    lat: loc.lat,
    lng: loc.lng,
    radiusM: loc.radiusM,
    name: loc.name,
    isPrimary: loc.isPrimary,
  }));

  const [overrideNote, setOverrideNote] = useState("");
  const [needOverride, setNeedOverride] = useState(false);
  const [geoDetails, setGeoDetails] = useState<GeofenceErrorDetails | null>(null);
  const [picker, setPicker] = useState<{
    coords: { lat: number; lng: number; accuracy?: number };
    candidates: AmbiguousCandidate[];
    resolver: { resolve: () => void; reject: (e: Error) => void } | null;
  } | null>(null);
  const [lastPickedLocationId, setLastPickedLocationId] = useState<string | null>(null);
  const [, setTick] = useState(0);

  const state = att.data;
  const hasAttendance = !!state?.attendance;
  const onLeaveToday = !!state?.onLeaveToday;
  const hasAssignedLocations = !!state?.hasAssignedLocations;
  // Leave-management is the sole whitelisted route while guard is active
  // (mirrors legacy UniversalLayout pattern). Modal silently hides there so
  // the user can file leave; the route guard handles forced redirect.
  const onLeavePage = location.pathname.startsWith("/leaves");
  const open =
    enabled &&
    att.isFetched &&
    !hasAttendance &&
    !onLeaveToday &&
    !onLeavePage;

  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [open]);

  // Geolocation is fetched only when the user actually slides to mark in.
  // No eager fetch — keeps the Request-leave / overlay-close paths usable
  // even if the device is slow to resolve GPS.

  const runMarkIn = (
    coords: { lat: number; lng: number; accuracy?: number },
    locationId?: string,
  ) => {
    const note = overrideNote.trim() || undefined;
    return new Promise<void>((resolve, reject) => {
      markIn.mutate(
        {
          lat: coords.lat,
          lng: coords.lng,
          accuracyM: coords.accuracy,
          overrideNote: note,
          locationId,
        },
        {
          onSuccess: () => {
            toast.success("Marked in");
            setOverrideNote("");
            setNeedOverride(false);
            setGeoDetails(null);
            setPicker(null);
            setLastPickedLocationId(null);
            resolve();
          },
          onError: (e: unknown) => {
            const cands = asGeofenceAmbiguousError(e);
            if (cands && cands.length > 1) {
              setPicker({ coords, candidates: cands, resolver: null });
              reject(new Error("ambiguous"));
              return;
            }
            const gErr = asGeofenceError(e);
            if (gErr) {
              setNeedOverride(true);
              setGeoDetails(gErr);
              reject(new Error("override"));
              return;
            }
            toastApiError(e, "Mark-in failed");
            reject(new Error("submit"));
          },
        },
      );
    });
  };

  const buildCandidates = (
    coords: { lat: number; lng: number },
  ): AmbiguousCandidate[] =>
    myLocations
      .map((loc) => {
        const d = haversineM(
          { lat: coords.lat, lng: coords.lng },
          { lat: loc.lat, lng: loc.lng },
        );
        return {
          locationId: loc.id,
          name: loc.name,
          distanceM: Math.round(d),
          radiusM: loc.radiusM,
          inside: d <= loc.radiusM,
          isPrimary: loc.isPrimary,
        } satisfies AmbiguousCandidate;
      })
      .sort((a, b) => a.distanceM - b.distanceM);

  const submit = async () => {
    let coords;
    try {
      coords = await geo.fetch();
    } catch {
      throw new Error("geo");
    }
    if (myLocations.length > 1) {
      if (needOverride && lastPickedLocationId) {
        return runMarkIn(coords, lastPickedLocationId);
      }
      const candidates = buildCandidates(coords);
      return new Promise<void>((resolve, reject) => {
        setPicker({ coords, candidates, resolver: { resolve, reject } });
      });
    }
    return runMarkIn(coords);
  };

  const onPickConfirm = async (locationId: string) => {
    if (!picker) return;
    setLastPickedLocationId(locationId);
    try {
      await runMarkIn(picker.coords, locationId);
      picker.resolver?.resolve();
    } catch (e) {
      picker.resolver?.reject(e as Error);
    } finally {
      setPicker(null);
    }
  };

  const onPickCancel = () => {
    picker?.resolver?.reject(new Error("cancelled"));
    setPicker(null);
  };

  // `mutating` = mark-in API call in flight. Dismiss / leave-request paths
  // gate on this only, NOT `geo.loading`, so slow GPS doesn't trap the user.
  const mutating = markIn.isPending;
  const slideBusy = geo.loading || markIn.isPending;
  if (!open) return null;

  const today = new Date();
  const inFenceUnknown = geoDetails == null && !needOverride;
  const noLocationBlock = !hasAssignedLocations;

  const goRequestLeave = () => {
    navigate("/leaves?new=1");
  };

  return createPortal(
    <div className="wm-modal-overlay">
      <div
        className="wm-modal"
        style={{
          width: "min(560px, calc(100vw - 24px))",
          maxHeight: "calc(100vh - 24px)",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="wm-modal-header">
          <div className="wm-modal-icon">
            <svg
              viewBox="0 0 16 16"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <circle cx="8" cy="8" r="6.5" />
              <path d="M8 4v4l2.5 2" strokeLinecap="round" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="wm-modal-title">Mark attendance — in</div>
            <div className="wm-modal-sub">
              Required to continue. Cannot be dismissed.
            </div>
          </div>
          <span className="wm-modal-lock">REQUIRED</span>
        </div>

        {/* Body */}
        <div className="wm-modal-body">
          {/* Clock + date */}
          <div className="wm-mi-clock">
            <div className="l">
              <div className="t">Today</div>
              <div className="v">
                {today.toLocaleDateString("en-IN", {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })}
              </div>
            </div>
            <div className="r">
              <div className="t">Current time</div>
              <div className="v mono">
                {today.getHours().toString().padStart(2, "0")}
                <span style={{ opacity: 0.4 }}>:</span>
                {today.getMinutes().toString().padStart(2, "0")}
                <span style={{ opacity: 0.4 }}>:</span>
                <span style={{ fontSize: ".55em", opacity: 0.6 }}>
                  {today.getSeconds().toString().padStart(2, "0")}
                </span>
              </div>
            </div>
          </div>

          {noLocationBlock && (
            <div className="wm-att-banner warn">
              <strong>No work location assigned.</strong>
              <span>
                You can't mark attendance yet. Ask HR / Operations to assign a
                location, or request leave for today.
              </span>
            </div>
          )}

          {/* Live location map */}
          <div className="wm-mi-loc-stack">
            <GeofenceLiveMap
              fences={fences}
              user={
                geo.lat != null && geo.lng != null
                  ? { lat: geo.lat, lng: geo.lng, accuracyM: geo.accuracy }
                  : null
              }
              within={needOverride ? false : inFenceUnknown ? null : true}
              height={220}
            />
            <div className="wm-mi-loc-body">
              <div className="wm-mi-loc-row">
                <span className="l">Closest fence</span>
                <span className="v">
                  {geoDetails?.closestLocationName ?? "—"}
                  {geoDetails?.closestRadiusM ? (
                    <span style={{ color: "var(--wm-ink-3)" }}>
                      {" "}
                      · radius {geoDetails.closestRadiusM}m
                    </span>
                  ) : null}
                </span>
              </div>
              <div className="wm-mi-loc-row">
                <span className="l">Distance</span>
                <span
                  className="v mono"
                  style={{
                    color: needOverride ? "var(--wm-danger)" : "var(--wm-ok)",
                    fontWeight: 600,
                  }}
                >
                  {geoDetails?.closestDistanceM != null
                    ? `${geoDetails.closestDistanceM}m · ${needOverride ? "OUTSIDE FENCE" : "within"}`
                    : needOverride
                      ? "Outside fence"
                      : geo.loading
                        ? "Locating…"
                        : "Will be checked on slide"}
                </span>
              </div>
            </div>
          </div>

          {needOverride && (
            <div className="wm-att-banner warn">
              <strong>Outside assigned location.</strong>
              <span>
                {geoDetails?.closestLocationName
                  ? `${geoDetails.closestDistanceM ?? "?"}m from ${geoDetails.closestLocationName}${geoDetails.closestRadiusM ? ` (radius ${geoDetails.closestRadiusM}m)` : ""}.`
                  : "No assigned location matched."}{" "}
                Add a note ≥10 chars and slide again.
              </span>
            </div>
          )}

          {!noLocationBlock && (
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--wm-ink-2)",
                  marginBottom: 6,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {needOverride ? "Override note (required ≥10 chars)" : "Note (optional)"}
              </label>
              <TextField
                multiline
                minRows={2}
                fullWidth
                size="small"
                value={overrideNote}
                onChange={(e) => setOverrideNote(e.target.value)}
                placeholder="Reason for being outside, or any context for today…"
                error={needOverride && overrideNote.trim().length < 10}
              />
            </div>
          )}

          {geo.error && !noLocationBlock && (
            <Alert severity="error" sx={{ fontSize: 12.5 }}>
              {geo.error}
            </Alert>
          )}
        </div>

        {/* Footer */}
        <div
          className="wm-modal-footer"
          style={{ flexDirection: "column", gap: 10, alignItems: "stretch" }}
        >
          {!noLocationBlock && (
            <SlideToConfirm
              tone="success"
              label={
                geo.loading
                  ? "Locating…"
                  : needOverride
                    ? "Slide to mark in (override)"
                    : "Slide to mark in"
              }
              completedLabel={markIn.isPending ? "Marking in…" : "Marked in"}
              loading={slideBusy}
              disabled={needOverride && overrideNote.trim().length < 10}
              onConfirm={submit}
            />
          )}
          <button
            type="button"
            className="wm-btn sm"
            onClick={goRequestLeave}
            disabled={mutating}
            style={{
              alignSelf: noLocationBlock ? "stretch" : "center",
              fontSize: 13,
              padding: "8px 14px",
            }}
          >
            Request leave for today instead
          </button>
        </div>

        <LocationPickerDialog
          open={!!picker}
          candidates={picker?.candidates ?? []}
          title="Pick mark-in location"
          message={
            (picker?.candidates ?? []).some((c) => c.inside === false)
              ? "You aren't inside any assigned office. Pick which one to record this attendance against — you'll be asked for an override note."
              : "Choose which office to record this attendance against."
          }
          busy={markIn.isPending}
          onCancel={onPickCancel}
          onConfirm={onPickConfirm}
        />
      </div>
    </div>,
    document.body,
  );
}
